import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverAnchor, PopoverContent } from './ui/popover';
import { parseTime, QUARTER_HOURS, nearestQuarter } from '../lib/timeText';

/**
 * A time box for the working-hours grid (Rob 1A, 03/11/2026): click it for a list in 15-minute
 * steps, or type ("9", "930", "13:30"). It replaces the browser's own time box and its clock /
 * "roller" pop-up. `value` and `onChange` are "HH:MM", exactly what the browser box gave, so the
 * grid's maths is untouched. A typed value is only applied when it reads as a time (Enter, Tab or
 * clicking away); anything else puts the old value back.
 */
const TimeField = ({ value, onChange, disabled = false, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value || '');

  useEffect(() => { setText(value || ''); }, [value]);

  // Open on the time already in the box. A ref CALLBACK, because the list lives in a portal and
  // only exists once the pop-up has opened — an effect ran too early and the list opened at 00:00.
  // scrollTop, not scrollIntoView, so the box behind it doesn't scroll too.
  const listRef = useCallback((node) => {
    if (!node) return;
    const el = node.querySelector(`[data-time="${nearestQuarter(value)}"]`);
    if (el) node.scrollTop = el.offsetTop - node.clientHeight / 2 + el.offsetHeight / 2;
  }, [value]);

  const commit = (raw) => {
    const t = parseTime(raw);
    if (t && t !== value) onChange(t);
    setText(t || value || '');
  };

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={`relative w-[84px] ${disabled ? 'opacity-40' : ''}`}>
          <input
            type="text"
            inputMode="numeric"
            value={text}
            disabled={disabled}
            aria-label={ariaLabel}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onChange={(e) => setText(e.target.value)}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(e.currentTarget.value); setOpen(false); }
              if (e.key === 'Escape') { setText(value || ''); setOpen(false); }
            }}
            className="w-full h-[30px] rounded-lg border border-input bg-field pl-2.5 pr-6 text-[13.5px] tabular-nums focus:outline-none focus:border-cta focus:ring-1 focus:ring-ring disabled:cursor-not-allowed"
          />
          <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[96px] p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div ref={listRef} className="max-h-[220px] overflow-y-auto" role="listbox" aria-label={ariaLabel}>
          {QUARTER_HOURS.map((t) => (
            <button
              key={t}
              type="button"
              role="option"
              aria-selected={t === value}
              data-time={t}
              // mousedown, so the box's blur doesn't put the old text back first
              onMouseDown={(e) => { e.preventDefault(); onChange(t); setText(t); setOpen(false); }}
              className={`w-full text-left px-2.5 py-1 rounded-md text-[13.5px] tabular-nums ${
                t === value ? 'bg-primary text-primary-foreground font-bold' : 'hover:bg-secondary'}`}
            >
              {t}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TimeField;
